import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCurrentYearAssumptionSetting } from './useCurrentYearAssumptionSettings';
import { ModalHeader } from '../OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../OPL/Modal/ModalCloseButton';
import { ModalBody } from '../OPL/Modal/ModalBody';
import { ModalFooter } from '../OPL/Modal/ModalFooter';
import { Select } from '../OPL/Select/Select/Select';
import { Toggle } from '../OPL/Toggle/Toggle';
import { Button } from '../OPL/Button/Button';
import { Modal } from '../OPL/Modal/Modal';
import { Box } from '../OPL/Box/Box';
import { FormValues } from './CurrentYearAssumptionSettings.meta';
import { ResponseDTO } from './api/responseDTO.types';
import { Text } from '../OPL/Text/Text';
import { HtmlTable } from '../OPL/HtmlTable/HtmlTable';
import { OptionType } from '../OPL/Select/BaseSelect/types';
import { toast } from '../OPL/Toast/Toast';
import { Alert } from '../OPL/Alert/Alert';
import { CurrentYearAssumptionsSettingsLoading } from './CurrentYearAssumptionsSettings.loading';

interface Props {
  onClose: () => void;
  isReadonly?: boolean;
}

export const CurrentYearAssumptionSetting = ({ onClose, isReadonly }: Props) => {
  const { data, isLoading } = useCurrentYearAssumptionSetting();

  if (isLoading) {
    return <CurrentYearAssumptionsSettingsLoading />;
  }

  if (!data) {
    return null;
  }

  return <CurrentYearAssumptionSettingForm onClose={onClose} data={data} isReadonly={isReadonly} />;
};

interface Data
  extends Omit<
    ResponseDTO,
    'contributionsOptions' | 'earningsOptions' | 'distributionsOptions' | 'taxesOptions'
  > {
  contributionsOptions: OptionType<number>[];
  earningsOptions: OptionType<number>[];
  distributionsOptions: OptionType<number>[];
  taxesOptions: OptionType<number>[];
  isReadonly?: boolean;
}

interface CurrentYearAssumptionSettingFormProps extends Props {
  data: Data;
}

const CurrentYearAssumptionSettingForm = ({
  data,
  onClose,
  isReadonly,
}: CurrentYearAssumptionSettingFormProps) => {
  const {
    action: {
      saveCurrentYearAssumptionMutation: {
        mutateAsync: saveCurrentYearAssumption,
        isLoading: isSavingCurrentYearAssumption,
        error: saveCurrentYearAssumptionError,
      },
    },
  } = useCurrentYearAssumptionSetting();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: data.currentYearAssumptions,
  });

  const onSubmit = handleSubmit(async values => {
    try {
      await saveCurrentYearAssumption(values);

      toast.success('Your settings was successfully saved');

      onClose();
    } catch (err) {
      console.error(err);
    }
  });

  return (
    <Modal onRequestClose={onClose} isOpen>
      <form onSubmit={onSubmit}>
        <ModalHeader as="h2" id="modal-header">
          Manage Current Year Assumptions
        </ModalHeader>
        <ModalCloseButton onClick={onClose} />

        <ModalBody>
          {saveCurrentYearAssumptionError && (
            <Alert variant="error" message={saveCurrentYearAssumptionError.message} />
          )}

          <HtmlTable variant="embedded" mx={-24} mt="16" width="auto">
            <table>
              <colgroup>
                <col width="0%" />
                <col width="100%" />
              </colgroup>

              <thead>
                <tr>
                  <th>
                    <Text variant="smallCaps" textAlign="left">
                      Category
                    </Text>
                  </th>
                  <th>
                    <Text variant="smallCaps" textAlign="left">
                      Current Year Assumptions
                    </Text>
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>Taxes</td>

                  <td>
                    <Controller
                      render={({ field: { onChange, value, ...field } }) => {
                        return (
                          <Select
                            {...field}
                            value={data.taxesOptions.find(option => option.value === value)}
                            onChange={opt => {
                              onChange(opt ? opt.value : '');
                            }}
                            options={data.taxesOptions}
                            formVariant={errors.taxesAssumptions && 'error'}
                            helperText={errors.taxesAssumptions?.message}
                            disabled={isReadonly}
                            isPortal
                            aria-label="Select Tax Assumption"
                          />
                        );
                      }}
                      name="taxesAssumptions"
                      control={control}
                    />
                  </td>
                </tr>

                <tr>
                  <td>Investment Earnings</td>

                  <td>
                    <Controller
                      render={({ field: { onChange, value, ...field } }) => (
                        <Select
                          {...field}
                          value={data.earningsOptions.find(option => option.value === value)}
                          onChange={opt => {
                            onChange(opt ? opt.value : '');
                          }}
                          options={data.earningsOptions}
                          formVariant={errors.earningsAssumptions && 'error'}
                          helperText={errors.earningsAssumptions?.message}
                          disabled={isReadonly}
                          isPortal
                          aria-label="Select Earning Assumption"
                        />
                      )}
                      name="earningsAssumptions"
                      control={control}
                    />
                  </td>
                </tr>

                <tr>
                  <td>Distributions</td>

                  <td>
                    <Controller
                      render={({ field: { onChange, value, ...field } }) => (
                        <Select
                          {...field}
                          value={data.distributionsOptions.find(option => option.value === value)}
                          onChange={opt => {
                            onChange(opt ? opt.value : '');
                          }}
                          options={data.distributionsOptions}
                          formVariant={errors.distributionsAssumptions && 'error'}
                          helperText={errors.distributionsAssumptions?.message}
                          disabled={isReadonly}
                          isPortal
                          aria-label="Select Distriibution Assumption"
                        />
                      )}
                      name="distributionsAssumptions"
                      control={control}
                    />
                  </td>
                </tr>

                <tr>
                  <td>Contributions</td>

                  <td>
                    <Controller
                      render={({ field: { onChange, value, ...field } }) => (
                        <Select
                          {...field}
                          value={data.contributionsOptions.find(option => option.value === value)}
                          onChange={opt => {
                            onChange(opt ? opt.value : '');
                          }}
                          options={data.contributionsOptions}
                          formVariant={errors.contributionsAssumptions && 'error'}
                          helperText={errors.contributionsAssumptions?.message}
                          disabled={isReadonly}
                          isPortal
                          aria-label="Select Contribution Assumption"
                        />
                      )}
                      name="contributionsAssumptions"
                      control={control}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </HtmlTable>

          <Box px="16">
            <Toggle
              {...register('revertToProRataForFutureYears')}
              disabled={isReadonly}
              mt="8"
              helperText="For subsequent current years, this setting divides cashflow activities from the categories
above evenly across the 12 months of the year."
            >
              Revert to pro-rata for future years
            </Toggle>
          </Box>
        </ModalBody>

        <ModalFooter>
          <Button variant="subtleInactive" onClick={onClose} mr="auto">
            Cancel
          </Button>
          {!isReadonly && (
            <>
              <Button
                ml="8"
                onClick={() => {
                  reset(data.defaultCurrentYearAssumptions);
                }}
              >
                Reset
              </Button>

              <Button ml="8" type="submit" isLoading={isSavingCurrentYearAssumption}>
                Save
              </Button>
            </>
          )}
        </ModalFooter>
      </form>
    </Modal>
  );
};
